import i18n from '@src/i18n';
import { landingConfigFor } from '@src/pages/LandingPage/configs';
import {
  ACCOUNT_BOXES_PATH,
  ACCOUNT_PATH,
  FAQ_PATH,
  PREMIUM_PATH,
  LOGIN_PATH,
  POLICY_PATH,
  TERMS_PATH,
  PRIVACY_POLICY_PATH,
  RANDOMIZER_PATH,
  RECOVER_PATH,
  REGISTER_PATH,
  UI_KIT_PATH,
  normalizePath,
} from '@src/routing/routes';
import { ROBOTS_NOINDEX_CONTENT, shouldNoIndex } from '@src/seo/noindex';
import { isDotCom, siteLanguage } from '@src/site';

const PRODUCTION_ORIGIN_RU = 'https://santa-secret.ru';
const PRODUCTION_ORIGIN_COM = 'https://santa-secret.com';

const PAGE_META_KEYS: Record<string, string> = {
  '/': 'home',
  '/open': 'open',
  '/open/recover': 'open',
  [LOGIN_PATH]: 'login',
  [REGISTER_PATH]: 'register',
  [RECOVER_PATH]: 'recover',
  [POLICY_PATH]: 'policy',
  [TERMS_PATH]: 'terms',
  [PRIVACY_POLICY_PATH]: 'policy',
  [FAQ_PATH]: 'faq',
  [PREMIUM_PATH]: 'premium',
  // '/gifts': 'gifts',
  [RANDOMIZER_PATH]: 'randomizer',
  [ACCOUNT_PATH]: 'account',
  [ACCOUNT_BOXES_PATH]: 'accountBoxes',
  [UI_KIT_PATH]: 'uiKit',
};

const HREFLANG_HOSTS: Record<string, string> = {
  en: PRODUCTION_ORIGIN_COM,
  ru: PRODUCTION_ORIGIN_RU,
  'x-default': PRODUCTION_ORIGIN_COM,
};

let hreflangLinks: HTMLLinkElement[] | null = null;

function productionOrigin(): string {
  return isDotCom() ? PRODUCTION_ORIGIN_COM : PRODUCTION_ORIGIN_RU;
}

function ensureMeta(selector: string, create: () => HTMLMetaElement): HTMLMetaElement {
  const existing = document.head.querySelector(selector);
  if (existing instanceof HTMLMetaElement) {
    return existing;
  }
  const el = create();
  document.head.appendChild(el);
  return el;
}

function ensureLink(rel: string): HTMLLinkElement {
  const existing = document.head.querySelector(`link[rel="${rel}"]`);
  if (existing instanceof HTMLLinkElement) {
    return existing;
  }
  const el = document.createElement('link');
  el.setAttribute('rel', rel);
  document.head.appendChild(el);
  return el;
}

function setTitle(title: string) {
  document.title = title;
}

function setDescription(content: string) {
  const el = ensureMeta('meta[name="description"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    return meta;
  });
  el.setAttribute('content', content);
}

function setOgUrl(href: string) {
  const el = ensureMeta('meta[property="og:url"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:url');
    return meta;
  });
  el.setAttribute('content', href);
}

function setCanonical(path: string) {
  const href = `${productionOrigin()}${path === '/' ? '/' : path}`;
  ensureLink('canonical').href = href;
  setOgUrl(href);
}

function setRobots(pathname: string) {
  const noindex = shouldNoIndex(pathname);
  let el = document.head.querySelector('meta[name="robots"]');
  if (noindex) {
    if (!(el instanceof HTMLMetaElement)) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', ROBOTS_NOINDEX_CONTENT);
    return;
  }
  el?.remove();
}

function setHreflangAlternates(path: string) {
  if (!hreflangLinks) {
    hreflangLinks = Array.from(document.head.querySelectorAll('link[rel="alternate"][hreflang]'));
    if (hreflangLinks.length === 0) {
      hreflangLinks = Object.keys(HREFLANG_HOSTS).map((lang) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        return link;
      });
    }
  }
  const shared = Boolean(PAGE_META_KEYS[path] || landingConfigFor(path)) && !shouldNoIndex(path);
  const indexableShared = shared && path !== UI_KIT_PATH;
  hreflangLinks.forEach((el) => {
    if (!indexableShared || (isDotCom() && el.getAttribute('hreflang') === 'ru')) {
      el.remove();
      return;
    }
    const host = HREFLANG_HOSTS[el.getAttribute('hreflang') || ''];
    if (host) {
      el.setAttribute('href', `${host}${path === '/' ? '/' : path}`);
    }
    if (!el.parentNode) {
      document.head.appendChild(el);
    }
  });
}

function setHtmlLang() {
  document.documentElement.setAttribute('lang', siteLanguage());
}

export function pageCopy(pathname: string): { title: string; description: string } {
  if (pathname === '/admin') return { title: i18n.t('admin.title'), description: '' };
  if ((pathname === '/premium/buy' || pathname.startsWith('/premium/buy/')))
    return { title: i18n.t('premiumBuy.title'), description: i18n.t('premiumBuy.signInBody') };
  const landing = landingConfigFor(pathname);
  if (landing) {
    return {
      title: i18n.t(`landing.${landing.slug}.meta.title`),
      description: i18n.t(`landing.${landing.slug}.meta.description`),
    };
  }
  const key = PAGE_META_KEYS[pathname];
  if (key) {
    return {
      title: i18n.t(`meta.${key}.title`),
      description: i18n.t(`meta.${key}.description`),
    };
  }
  return {
    title: i18n.t('meta.home.title'),
    description: '',
  };
}

/** Title, description, and robots flag for the HTML shell — used by SSR generate. */
export function pageMeta(pathname: string): {
  title: string;
  description: string;
  noindex: boolean;
} {
  const path = normalizePath(pathname);
  const { title, description } = pageCopy(path);
  return { title, description, noindex: shouldNoIndex(path) };
}

/** Title, description, canonical, og:url, robots, hreflang, html lang. */
export function updateMetaTags(pathname: string = window.location.pathname): void {
  const path = normalizePath(pathname);
  const { title, description } = pageCopy(path);
  setTitle(title);
  setDescription(description);
  setCanonical(path);
  setHreflangAlternates(path);
  setRobots(path);
  setHtmlLang();
}
